import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

type Feedback = {
  id: string
  order_id: string
  rating: number
  comment: string | null
  reviewer_name: string
  created_at: string
  is_visible: boolean
  service_name?: string
}

const STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n)

const AdminFeedbacksPage: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState<Set<string>>(new Set())

  const loadFeedbacks = async () => {
    setLoading(true)

    const { data, error } = await supabase
      .from('order_feedbacks')
      .select(`
        id,
        order_id,
        rating,
        comment,
        reviewer_name,
        created_at,
        is_visible,
        order:orders!order_feedbacks_order_id_fkey (
          service:services!fk_orders_service (name)
        )
      `)
      .order('created_at', { ascending: false })

    if (error) {
      console.error(error)
      setLoading(false)
      return
    }

    const mapped: Feedback[] = (data ?? []).map((f: any) => {
      const order = Array.isArray(f.order) ? f.order[0] : f.order
      const service = order
        ? Array.isArray(order.service) ? order.service[0] : order.service
        : null
      return {
        id: f.id,
        order_id: f.order_id,
        rating: f.rating,
        comment: f.comment,
        reviewer_name: f.reviewer_name,
        created_at: f.created_at,
        is_visible: f.is_visible,
        service_name: service?.name ?? '—',
      }
    })

    setFeedbacks(mapped)
    setLoading(false)
  }

  useEffect(() => {
    loadFeedbacks()
  }, [])

  const toggleVisibility = async (feedback: Feedback) => {
    setToggling(prev => new Set(prev).add(feedback.id))

    const { error } = await supabase
      .from('order_feedbacks')
      .update({ is_visible: !feedback.is_visible })
      .eq('id', feedback.id)

    if (!error) {
      setFeedbacks(prev =>
        prev.map(f => f.id === feedback.id ? { ...f, is_visible: !f.is_visible } : f)
      )
    }

    setToggling(prev => {
      const next = new Set(prev)
      next.delete(feedback.id)
      return next
    })
  }

  const visible = feedbacks.filter(f => f.is_visible).length

  return (
    <div className="card">
      <div className="card-header">
        <h1 className="card-title">Avis clients</h1>
        <p style={{ color: '#94a3b8', marginTop: '.25rem', fontSize: '.9rem' }}>
          {visible} avis visible{visible !== 1 ? 's' : ''} sur {feedbacks.length} au total
        </p>
      </div>

      {loading && <p style={{ color: '#94a3b8', marginTop: '1rem' }}>Chargement…</p>}

      {!loading && feedbacks.length === 0 && (
        <p style={{ color: '#94a3b8', marginTop: '1rem' }}>
          Aucun avis reçu pour l'instant.
        </p>
      )}

      {!loading && feedbacks.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem', marginTop: '1rem' }}>
          <thead style={{ background: '#0f172a', color: '#f8fafc' }}>
            <tr>
              <th style={{ padding: '.5rem', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '.5rem', textAlign: 'left' }}>Client</th>
              <th style={{ padding: '.5rem', textAlign: 'left' }}>Service</th>
              <th style={{ padding: '.5rem', textAlign: 'left' }}>Note</th>
              <th style={{ padding: '.5rem', textAlign: 'left' }}>Commentaire</th>
              <th style={{ padding: '.5rem', textAlign: 'center' }}>Visible</th>
            </tr>
          </thead>
          <tbody>
            {feedbacks.map(f => (
              <tr
                key={f.id}
                style={{
                  borderBottom: '1px solid rgba(148,163,184,0.15)',
                  opacity: f.is_visible ? 1 : 0.45,
                  transition: 'opacity .2s',
                }}
              >
                <td style={{ padding: '.5rem', whiteSpace: 'nowrap' }}>
                  {new Date(f.created_at).toLocaleDateString('fr-CH')}
                </td>
                <td style={{ padding: '.5rem' }}>{f.reviewer_name}</td>
                <td style={{ padding: '.5rem', color: '#94a3b8' }}>{f.service_name}</td>
                <td style={{ padding: '.5rem', color: '#f59e0b', letterSpacing: '2px' }}>
                  {STARS(f.rating)}
                </td>
                <td style={{ padding: '.5rem', color: '#cbd5e1', maxWidth: 300 }}>
                  {f.comment || <span style={{ color: '#475569' }}>—</span>}
                </td>
                <td style={{ padding: '.5rem', textAlign: 'center' }}>
                  <button
                    className={`btn ${f.is_visible ? 'btn-primary' : 'btn-ghost'}`}
                    style={{ fontSize: '.8rem', padding: '.2rem .6rem' }}
                    disabled={toggling.has(f.id)}
                    onClick={() => toggleVisibility(f)}
                    title={f.is_visible ? 'Masquer sur la page d\'accueil' : 'Afficher sur la page d\'accueil'}
                  >
                    {f.is_visible ? '👁 Visible' : '🚫 Masqué'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default AdminFeedbacksPage
